/**
 * `<SurveyStep>` — single dispatch component for the JSON-driven funnel.
 *
 * Looks up a renderer keyed on `step.type` from the supplied registry,
 * validates the JSON `data` payload via the renderer's Zod schema, and
 * forwards typed props to the renderer's `Component`.
 *
 * Per `@/Users/karlbernard/workspace/nextday-onboarding/docs/PLAN.md` §6.2:
 * "A single `<SurveyStep config={step} />` component that switches on
 * `step.type`. Each question is a self-contained sub-component."
 *
 * Concrete renderers ship in NX-9301..NX-9304; this file owns the dispatch
 * + validation plumbing and the dev-time fallback when a renderer is missing
 * or the data payload fails schema validation.
 */
import { memo, type ReactElement } from "react";
import type { ZodType } from "zod";
import { installZod3ErrorMap } from "@nextday/zod-compat";
import { lookupRenderer } from "@survey-kit/registry";
import type {
  AnswerInput,
  RendererProps,
  SessionContext,
  Step,
  StepRendererRegistry,
} from "@survey-kit/types";
import { useTransformedData } from "@survey-kit/useTransformedData";
import type { FunnelState } from "@nextday/funnel-engine";

installZod3ErrorMap();

export type SurveyStepProps = {
  /** Step JSON resolved by the funnel engine for the current position. */
  step: Step;
  /** Renderer registry — frozen, built via `defineRegistry()`. */
  registry: StepRendererRegistry;
  /** All answers captured so far, keyed by `answerKey`. */
  answers: Record<string, unknown>;
  /** Latest session context (character, brand, ids). */
  session: SessionContext;
  /** Engine state for renderers that need progress / history. */
  funnelState: FunnelState;
  onAnswer: (answerKey: string, input: AnswerInput) => void;
  onAdvance: () => void;
  onSkip?: () => void;
  onValidityChange?: (valid: boolean) => void;
  onRestart?: () => void;
  /**
   * Override the default dev-time error renderer. When omitted, missing
   * renderers and failing schema validation render a `<MissingRenderer>` /
   * `<InvalidStepData>` placeholder so the funnel doesn't crash.
   */
  fallback?: (error: SurveyStepFallback) => ReactElement | null;
};

export type SurveyStepFallback =
  | { kind: "missing_renderer"; type: string; step: Step }
  | { kind: "invalid_data"; type: string; step: Step; issues: string[] };

const formatIssues = (
  issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>,
): string[] =>
  issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join(".") : "(root)";
    return `${path}: ${issue.message}`;
  });

const DefaultFallback = (params: SurveyStepFallback): ReactElement => {
  if (params.kind === "missing_renderer") {
    return (
      <div role="alert" data-survey-step-error="missing_renderer" className="p-4 text-sm">
        <strong>survey-kit:</strong> <span>no renderer registered for StepType</span>{" "}
        <code>{params.type}</code>
        <span>.</span>
      </div>
    );
  }
  return (
    <div role="alert" data-survey-step-error="invalid_data" className="p-4 text-sm">
      <strong>survey-kit:</strong> <span>invalid</span> <code>data</code>{" "}
      <span>payload for step</span> <code>{params.step.id}</code> <span>(type</span>{" "}
      <code>{params.type}</code>
      <span>):</span>
      <ul className="mt-2 list-disc pl-5">
        {params.issues.map((issue) => (
          <li key={issue}>{issue}</li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Validate a step's `data` against its renderer schema. Returns the typed
 * payload on success or a list of human-readable issues on failure.
 */
const validateStepData = <TData,>(
  schema: ZodType<TData>,
  data: unknown,
): { ok: true; data: TData } | { ok: false; issues: string[] } => {
  const result = schema.safeParse(data);
  if (result.success) return { ok: true, data: result.data };
  return { ok: false, issues: formatIssues(result.error.issues) };
};

const SurveyStepImpl = ({
  step,
  registry,
  answers,
  session,
  funnelState,
  onAnswer,
  onAdvance,
  onSkip,
  onValidityChange,
  onRestart,
  fallback,
}: SurveyStepProps): ReactElement | null => {
  const transformedData = useTransformedData(step.data, answers);
  const renderer = lookupRenderer(registry, step.type);
  const renderFallback = fallback ?? DefaultFallback;

  if (!renderer) {
    return renderFallback({ kind: "missing_renderer", type: step.type, step });
  }

  const validation = validateStepData(renderer.dataSchema, transformedData);
  if (!validation.ok) {
    return renderFallback({
      kind: "invalid_data",
      type: step.type,
      step,
      issues: validation.issues,
    });
  }

  const props: RendererProps<unknown> = {
    data: validation.data,
    step,
    answers,
    session,
    funnelState,
    onAnswer,
    onAdvance,
    ...(onSkip ? { onSkip } : {}),
    ...(onValidityChange ? { onValidityChange } : {}),
    ...(onRestart ? { onRestart } : {}),
  };
  // Render as JSX so React creates a component boundary in the fiber
  // tree. Calling `renderer.Component(props)` directly would register any
  // hooks in the renderer against `SurveyStepImpl`'s fiber, and switching
  // between two renderers with different hook counts would crash with
  // "Rendered more/fewer hooks than during the previous render". The
  // upcoming concrete renderers in NX-9301+ all use hooks.
  //
  // `key={step.id}` forces React to remount when the funnel navigates
  // between two steps of the same `StepType` (e.g. two consecutive
  // `single_choice` steps that resolve to the same `Renderer` reference
  // from the registry). Without the key, React would reuse the fiber and
  // any `useState` / `useRef` inside the renderer would leak previous-step
  // state into the next step's first render.
  const Renderer = renderer.Component;
  return <Renderer key={step.id} {...props} />;
};

export const SurveyStep = memo(SurveyStepImpl);

export default SurveyStep;
